import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import { useCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { useStoreStatus } from "../lib/storeStatus";
import { Minus, Plus, ArrowRight, TrendingUp, Crown, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_MILESTONES = [1, 5, 10, 20, 50, 100];
const MILESTONE_LABEL = { 1: "STANDARD", 5: "BULK", 10: "POPULAR", 20: "BETTER VALUE", 50: "BEST VALUE", 100: "MAXIMUM VALUE", 200: "MEGA PACK" };

export default function ApexConfigurator() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { format, code, isBase } = useCurrency();
  const { user } = useAuth();
  const { salesPaused } = useStoreStatus();
  const [product, setProduct] = useState(null);
  // Honour an initial ?qty=N query param (Apex Finder deep-links here with a
  // preselected quantity so the customer lands on this page with the exact
  // recommendation already configured — one stage before Checkout).
  const initialQty = (() => {
    const raw = parseInt(params.get("qty") || "1", 10);
    // Upper-bound is refined once the quote lands (max_quantity field);
    // 200 keeps the deep-link honest for products with extended ladders.
    return Number.isFinite(raw) ? Math.max(1, Math.min(200, raw)) : 1;
  })();
  const [qty, setQty] = useState(initialQty);
  const [input, setInput] = useState(String(initialQty));
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");
  // Per-product cap — derived from the quote's `max_quantity` (default 100
  // for every existing product; 200 for the 499 Apex pack). Falls back to
  // the product's own tier data if the quote hasn't landed yet.
  const maxQty = quote?.max_quantity
    ?? (product ? Math.max(...(product.tiers || []).map(t => t.max_qty)) : 100)
    ?? 100;

  useEffect(() => { api.get(`/apex-products/${id}`).then(r => setProduct(r.data)).catch(() => setErr("Product not found")); }, [id]);

  const fetchQuote = useCallback(async (q) => {
    if (!product) return;
    try {
      const { data } = await api.post("/apex-quote", { apex_product_id: id, quantity: q });
      setQuote(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Quote failed"); }
  }, [id, product]);

  useEffect(() => { if (product) fetchQuote(qty); }, [qty, product, fetchQuote]);

  const setQ = (n) => {
    const v = Math.max(1, Math.min(maxQty, Math.floor(Number(n) || 1)));
    setQty(v); setInput(String(v));
  };
  const onInput = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    setInput(raw);
    if (raw === "") return;
    const v = parseInt(raw, 10);
    if (Number.isFinite(v)) setQ(v);
  };

  if (err) return <div><Header/><div className="pt-32 text-center text-red-400">{err}</div></div>;
  if (!product || !quote) return <div><Header/><div className="pt-32 text-center text-[var(--av-text-dim)]">Loading…</div></div>;
  // Belt-and-braces: quote MUST have current_tier + tier_rate. Guard so a
  // partial backend response can never blank the customer's screen — the
  // ErrorBoundary is the safety net but returning a soft-fail here gives
  // them a retry-able message instead.
  if (!quote.current_tier || quote.tier_rate === undefined) {
    return <div><Header/><div className="pt-32 text-center text-[var(--av-text-dim)]">Pricing temporarily unavailable — please refresh.</div></div>;
  }

  const proceed = () => nav(`/checkout?product_type=apex&product_id=${product.id}&qty=${qty}`);
  const nt = quote.next_tier;
  const rrpTotal = (product.rrp_unit_gbp || 0) * qty;
  const showSaving = rrpTotal > 0 && rrpTotal > quote.total_price_gbp;
  const savingPct = showSaving ? Math.max(0, Math.min(99, Math.round((1 - quote.total_price_gbp / rrpTotal) * 100))) : 0;
  // Server-enforced single-order cap (default £1,000). If the customer's
  // chosen quantity would breach it we disable Continue and show a nudge
  // pointing them at a lower quantity. The backend rejects the order at
  // /checkout/quote and /checkout/create too — this is UX, not security.
  const maxOrderGbp = quote.max_order_gbp ?? 1000;
  const overCap = !!quote.exceeds_order_limit;

  return (
    <div>
      <Header/>
      <div className="max-w-5xl mx-auto pt-28 pb-16 px-3 sm:px-8 min-w-0">
        <Link to="/store" className="mono-caption text-[var(--av-text-dim)] hover:text-[var(--av-gold)]">← Apex Store</Link>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-6 break-words"><span className="gold-text">{product.apex_amount.toLocaleString()} Apex</span> <span className="text-[var(--av-text-dim)] text-xl sm:text-2xl">per pack</span></h1>

        <div className="grid lg:grid-cols-5 gap-6 min-w-0">
          {/* Quantity + nudge */}
          <div className="lg:col-span-3 space-y-6 min-w-0">
            <div className="vault-card p-4 sm:p-6 min-w-0">
              <div className="mono-caption mb-4">Choose Quantity <span className="text-[var(--av-text-muted)]">(1–{maxQty})</span></div>
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <button data-testid="qty-dec" onClick={()=>setQ(qty-1)} disabled={qty<=1} className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full border border-[var(--av-border-strong)] flex items-center justify-center text-[var(--av-gold)] hover:bg-[rgba(212,175,55,0.08)] disabled:opacity-30"><Minus className="w-5 h-5"/></button>
                <input data-testid="qty-input" type="text" inputMode="numeric" value={input} onChange={onInput}
                  onBlur={()=>setQ(input||1)}
                  size="1"
                  className="w-0 flex-1 min-w-0 text-center text-4xl sm:text-5xl font-serif font-bold gold-text bg-transparent border-none focus:outline-none"/>
                <button data-testid="qty-inc" onClick={()=>setQ(qty+1)} disabled={qty>=maxQty} className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full border border-[var(--av-border-strong)] flex items-center justify-center text-[var(--av-gold)] hover:bg-[rgba(212,175,55,0.08)] disabled:opacity-30"><Plus className="w-5 h-5"/></button>
              </div>
              {/* Slider */}
              <input type="range" min="1" max={maxQty} step="1" value={qty} onChange={e=>setQ(e.target.value)} data-testid="qty-slider" className="w-full mt-6"/>
              {/* Milestones — driven by quote.milestones so per-product
                  ladders (e.g. 499 has an extra 200-pack) surface without
                  frontend hardcoding. Grid columns adapt to milestone count. */}
              {(() => {
                const ms = (quote.milestones && quote.milestones.length)
                  ? quote.milestones.map(m => m.quantity)
                  : FALLBACK_MILESTONES;
                const colCls = ms.length >= 7
                  ? "grid-cols-4 sm:grid-cols-7"
                  : "grid-cols-3 sm:grid-cols-6";
                return (
                  <div className={`grid ${colCls} gap-2 mt-5`}>
                    {ms.map(m => (
                      <button key={m} data-testid={`milestone-${m}`} onClick={()=>setQ(m)} className={`p-2 rounded-lg border text-center transition min-w-0 ${qty===m?"border-[var(--av-gold)] bg-[rgba(212,175,55,0.1)]":"border-[var(--av-border)] hover:border-[var(--av-gold)]"}`}>
                        <div className="font-serif text-lg font-bold">{m}×</div>
                        <div className="text-[9px] text-[var(--av-text-muted)] mono-caption leading-tight break-words">{MILESTONE_LABEL[m] || `${m}-PACK`}</div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Nudge */}
            {nt ? (
              <div className="vault-card p-6 border-[var(--av-purple)]">
                <div className="flex items-center gap-2 mono-caption purple-text mb-3">
                  <TrendingUp className="w-4 h-4"/>
                  {qty >= nt.next_quantity - 2 ? "ALMOST AT BEST VALUE" : "NEXT BULK RATE"}
                </div>
                <div className="font-serif text-xl font-bold mb-2">
                  Add {nt.additional_packs} more pack{nt.additional_packs>1?"s":""} for only <span className="gold-text">{format(nt.incremental_cost_gbp)}</span> more.
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 my-4 text-sm">
                  <div><div className="mono-caption">Additional Apex</div><div className="font-serif text-xl gold-text">+{nt.additional_apex.toLocaleString()}</div></div>
                  <div><div className="mono-caption">New unit rate</div><div className="font-serif text-xl gold-text">{format(nt.next_unit_price_gbp)}</div></div>
                  <div><div className="mono-caption">Bulk-rate saving</div><div className="font-serif text-xl purple-text">{format(nt.bulk_rate_saving_gbp)}</div></div>
                </div>
                {quote.next_tier_rewards && quote.next_tier_rewards.additional_rewards > 0 && (
                  <div className="text-xs text-[var(--av-text-dim)] mb-4">Plus earn approx <span className="purple-text">{format(quote.next_tier_rewards.additional_rewards)}</span> more Apex Rewards.</div>
                )}
                <button data-testid="nudge-upgrade" onClick={()=>setQ(nt.next_quantity)} className="btn-purple">
                  {qty >= nt.next_quantity - 2 ? `Upgrade to ${nt.next_quantity}` : `Add ${nt.additional_packs} pack${nt.additional_packs>1?"s":""}`}
                </button>
              </div>
            ) : (
              <div className="vault-card p-6 border-[var(--av-gold)]">
                <div className="mono-caption gold-text mb-2 flex items-center gap-2"><Crown className="w-4 h-4"/> MAXIMUM VALUE UNLOCKED</div>
                <p className="text-sm text-[var(--av-text-dim)]">You have unlocked Arthur's Vault's maximum standard bulk rate for this Apex pack.</p>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="lg:col-span-2 min-w-0">
            <div className="vault-card p-4 sm:p-6 lg:sticky lg:top-24 space-y-3 min-w-0">
              <div className="mono-caption">Live Summary</div>
              <div>
                <div className="text-xs text-[var(--av-text-dim)]">Total Apex</div>
                <div className="font-serif text-3xl font-bold gold-text">{quote.total_apex.toLocaleString()}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm pt-2">
                <div><div className="mono-caption">Quantity</div><div>{quote.quantity} × {product.apex_amount.toLocaleString()}</div></div>
                <div><div className="mono-caption">Per pack</div><div>{format(quote.unit_price_gbp)}</div></div>
              </div>
              <div className="divider-gold my-2"></div>
              <div>
                <div className="mono-caption">Total Price</div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="font-serif text-3xl font-bold">{format(quote.total_price_gbp)}</div>
                  {showSaving && (
                    <div className="text-lg text-[var(--av-text-muted)] line-through decoration-[var(--av-purple)]/70">{format(rrpTotal)}</div>
                  )}
                </div>
                {showSaving && (
                  <div className="text-[11px] purple-text font-semibold mt-1">You save {format(rrpTotal - quote.total_price_gbp)} ({savingPct}%)</div>
                )}
                {!isBase && <div className="text-[11px] text-[var(--av-text-muted)] mt-1">£{Number(quote.total_price_gbp || 0).toFixed(2)} GBP</div>}
              </div>
              {/* MAX 5% APEX REWARDS CALLOUT — deliberately loud so
                  customers know the effective price BEFORE they leave
                  this page. 5% is the platform-wide cap on rewards
                  redemption per order (see pricing.compute_max_redemption). */}
              {quote.total_price_gbp > 0 && (() => {
                const maxRedeem = quote.total_price_gbp * 0.05;
                const priceAfter = quote.total_price_gbp - maxRedeem;
                const perPackAfter = priceAfter / (quote.quantity || 1);
                return (
                  <div data-testid="max-rewards-callout"
                       className="rounded-xl border-2 border-[var(--av-purple)] bg-[rgba(148,0,211,0.10)] p-4 mt-2 relative overflow-hidden">
                    <div className="mono-caption purple-text mb-1 flex items-center gap-1">
                      <Sparkles className="w-4 h-4"/> WITH MAX 5% APEX REWARDS
                    </div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div data-testid="max-rewards-price"
                           className="font-serif text-4xl font-extrabold purple-text leading-none">
                        {format(priceAfter)}
                      </div>
                      <div className="text-sm text-[var(--av-text-muted)] line-through">
                        {format(quote.total_price_gbp)}
                      </div>
                    </div>
                    {/* Per-pack effective price after the max 5% discount */}
                    <div className="mt-3 pt-3 border-t border-[var(--av-purple)]/40 flex items-baseline justify-between gap-2">
                      <div className="mono-caption purple-text">Cost per pack</div>
                      <div className="flex items-baseline gap-2">
                        <div data-testid="max-rewards-per-pack"
                             className="font-serif text-2xl font-bold purple-text leading-none">
                          {format(perPackAfter)}
                        </div>
                        <div className="text-xs text-[var(--av-text-muted)] line-through">
                          {format(quote.unit_price_gbp)}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs mt-3 text-[var(--av-text)]">
                      Redeem up to <span className="purple-text font-bold">{format(maxRedeem)}</span> in Apex Rewards on this order — that's <span className="purple-text font-bold">5% off</span> at checkout.
                    </div>
                    {(!user || !(user.reward_available > 0)) && (
                      <div className="text-[10px] text-[var(--av-text-dim)] mt-1">
                        {user ? "Rewards balance builds up with every order." : "Sign in at checkout to redeem existing rewards."}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="pill pill-gold w-full justify-center">{quote.current_tier?.label || `TIER ${quote.current_tier?.min_qty || 1}+`} · {quote.tier_rate ?? 1}% back</div>
              <div className="text-xs text-[var(--av-text-dim)] flex items-center gap-1"><Sparkles className="w-3 h-3 text-[var(--av-purple)]"/> Est. Apex Rewards: <span className="purple-text font-semibold">{format(quote.rewards_earned)}</span></div>
              {overCap && (
                <div data-testid="order-limit-warning" className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-xs text-red-200">
                  <div className="font-semibold text-red-300 mb-1">Single-order limit reached</div>
                  <div>Maximum order value is <span className="font-semibold">£{Number(maxOrderGbp).toLocaleString()}</span>. Please reduce the quantity, or split this into more than one order.</div>
                </div>
              )}
              <button data-testid="apex-continue" onClick={proceed} disabled={overCap || salesPaused} className="btn-gold w-full mt-3 disabled:opacity-40 disabled:cursor-not-allowed">
                {salesPaused ? "Sales Paused" : (overCap ? `Over £${Number(maxOrderGbp).toLocaleString()} limit` : (<>Continue to Checkout <ArrowRight className="w-4 h-4 inline ml-1"/></>))}
              </button>
              <div className="text-xs text-[var(--av-text-dim)] flex justify-center items-center gap-1"><ShieldCheck className="w-3 h-3"/> Secure Stripe checkout · GBP</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
